const EmptyCartUpsell = {
  SECTION_ID: 'cart-drawer-empty-upsell',
  html: null,
  promise: null,

  slot(drawer) {
    return drawer.querySelector('[data-empty-upsell]:not([data-loaded])');
  },

  url() {
    const root = (window.Shopify && Shopify.routes && Shopify.routes.root) || '/';
    return `${root}?section_id=${this.SECTION_ID}`;
  },

  loadCss(href) {
    if (document.querySelector(`link[rel="stylesheet"][href="${href}"]`)) return Promise.resolve();
    return new Promise((resolve) => {
      const el = document.createElement('link');
      el.rel = 'stylesheet';
      el.href = href;
      el.onload = el.onerror = resolve;
      document.head.appendChild(el);
    });
  },

  // Fetch once, cache. CSS is loaded and awaited BEFORE the markup is ready.
  fetch() {
    if (this.html) return Promise.resolve(this.html);
    if (this.promise) return this.promise;

    this.promise = fetch(this.url())
      .then((r) => {
        if (!r.ok) throw new Error(r.status);
        return r.text();
      })
      .then((text) => {
        const doc = new DOMParser().parseFromString(text, 'text/html');
        const section = doc.querySelector('.shopify-section') || doc.body;
        const links = Array.from(section.querySelectorAll('link[rel="stylesheet"]'));
        const hrefs = links.map((l) => l.getAttribute('href'));
        links.forEach((l) => l.remove());
        return Promise.all(hrefs.map((h) => this.loadCss(h))).then(() => {
          this.html = section.innerHTML;
          return this.html;
        });
      })
      .catch((e) => {
        this.promise = null; // allow retry
        throw e;
      });

    return this.promise;
  },

  // Called on cart-icon hover/touch/focus — only fetches if cart is empty
  prefetch(drawer) {
    if (this.slot(drawer)) this.fetch().catch(() => {});
  },

  // Called when drawer opens or cart becomes empty
  load(drawer) {
    const slot = this.slot(drawer);
    if (!slot) return;

    if (this.html) {
      this.insert(slot);
      return;
    }

    // Skeleton shows until the fetch settles (text is no longer hidden, so no timer needed)
    drawer.classList.add('ceu-loading');

    this.fetch()
      .then(() => {
        const s = this.slot(drawer);
        if (s) this.insert(s);
      })
      .catch(() => {})
      .finally(() => drawer.classList.remove('ceu-loading'));
  },

    // Keep the drawer's is-empty class in sync with what Liquid rendered
  // (handles gift-only carts and re-renders from other scripts)
  sync(drawer) {
    const hasEmptyState = !!drawer.querySelector('.drawer__inner-empty');
    drawer.classList.toggle('is-empty', hasEmptyState);
    if (hasEmptyState && drawer.classList.contains('active')) this.load(drawer);
  },

  insert(slot) {
    slot.innerHTML = this.html;
    slot.setAttribute('data-loaded', '');
  },
};


class CartDrawer extends HTMLElement {
  constructor() {
    super();

    this.addEventListener('keyup', (evt) => evt.code === 'Escape' && this.close());
    this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
    this.setHeaderCartIconAccessibility();

    // Prefetch empty-cart upsell on intent (hover/touch/focus), not on page load
    const cartIcon = document.querySelector('#cart-icon-bubble');
    if (cartIcon) {
      ['pointerenter', 'touchstart', 'focusin'].forEach((evt) =>
        cartIcon.addEventListener(evt, () => EmptyCartUpsell.prefetch(this), { passive: true })
      );
    }

    // Re-sync only when the drawer containers themselves are re-rendered
    // (Dawn cart.js replaces .drawer__inner / #CartDrawer; gift manager swaps them too).
    // Changes deeper inside (counter, gifts accordion, swatches, our own insert) are ignored.
    let syncQueued = false;
    new MutationObserver((mutations) => {
      if (syncQueued) return;
      const containerChanged = mutations.some((m) =>
        m.target === this ||
        m.target.id === 'CartDrawer' ||
        (m.target.classList && m.target.classList.contains('drawer__inner'))
      );
      if (!containerChanged) return;
      syncQueued = true;
      requestAnimationFrame(() => {
        syncQueued = false;
        EmptyCartUpsell.sync(this);
      });
    }).observe(this, { childList: true, subtree: true });
  }

  setHeaderCartIconAccessibility() {
    const cartLink = document.querySelector('#cart-icon-bubble');
    if (!cartLink) return;

    cartLink.setAttribute('role', 'button');
    cartLink.setAttribute('aria-haspopup', 'dialog');
    cartLink.addEventListener('click', (event) => {
      event.preventDefault();
      this.open(cartLink);
    });
    cartLink.addEventListener('keydown', (event) => {
      if (event.code.toUpperCase() === 'SPACE') {
        event.preventDefault();
        this.open(cartLink);
      }
    });
  }

  open(triggeredBy) {
    EmptyCartUpsell.load(this);
    if (triggeredBy) this.setActiveElement(triggeredBy);
    const cartDrawerNote = this.querySelector('[id^="Details-"] summary');
    if (cartDrawerNote && !cartDrawerNote.hasAttribute('role')) this.setSummaryAccessibility(cartDrawerNote);
    // here the animation doesn't seem to always get triggered. A timeout seem to help
    setTimeout(() => {
      this.classList.add('animate', 'active');
    });

    this.addEventListener(
      'transitionend',
      () => {
        const containerToTrapFocusOn = this.classList.contains('is-empty')
          ? this.querySelector('.drawer__inner-empty')
          : document.getElementById('CartDrawer');
        const focusElement = this.querySelector('.drawer__inner') || this.querySelector('.drawer__close');
        trapFocus(containerToTrapFocusOn, focusElement);
      },
      { once: true }
    );

    document.body.classList.add('overflow-hidden');
  }

  close() {
    this.classList.remove('active');
    removeTrapFocus(this.activeElement);
    document.body.classList.remove('overflow-hidden');
  }

  setSummaryAccessibility(cartDrawerNote) {
    cartDrawerNote.setAttribute('role', 'button');
    cartDrawerNote.setAttribute('aria-expanded', 'false');

    if (cartDrawerNote.nextElementSibling.getAttribute('id')) {
      cartDrawerNote.setAttribute('aria-controls', cartDrawerNote.nextElementSibling.id);
    }

    cartDrawerNote.addEventListener('click', (event) => {
      event.currentTarget.setAttribute('aria-expanded', !event.currentTarget.closest('details').hasAttribute('open'));
    });

    cartDrawerNote.parentElement.addEventListener('keyup', onKeyUpEscape);
  }

  renderContents(parsedState) {
    this.querySelector('.drawer__inner').classList.contains('is-empty') &&
      this.querySelector('.drawer__inner').classList.remove('is-empty');
    this.productId = parsedState.id;
    // The add-to-cart response (sections/cart-drawer-add) leaves out the free-gift
    // rules snippet; keep the hide-gift <style> that is already on the page.
    const keptGiftStyle = document.getElementById('nobl-gift-hide');
    this.getSectionsToRender().forEach((section) => {
      const sectionElement = section.selector
        ? document.querySelector(section.selector)
        : document.getElementById(section.id);

      if (!sectionElement) return;
      sectionElement.innerHTML = this.getSectionInnerHTML(parsedState.sections[section.id], section.selector);
    });
    if (keptGiftStyle && !document.getElementById('nobl-gift-hide')) {
      (this.querySelector('.drawer__inner') || this).appendChild(keptGiftStyle);
    }

    setTimeout(() => {
      this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
      this.open();
    });
  }

  getSectionInnerHTML(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector).innerHTML;
  }

  getSectionsToRender() {
    return [
      {
        id: 'cart-drawer',
        selector: '#CartDrawer',
      },
      {
        id: 'cart-icon-bubble',
      },
    ];
  }

  getSectionDOM(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector);
  }

  setActiveElement(element) {
    this.activeElement = element;
  }
}

customElements.define('cart-drawer', CartDrawer);

class CartDrawerItems extends CartItems {
  getSectionsToRender() {
    return [
      {
        id: 'CartDrawer',
        section: 'cart-drawer',
        selector: '.drawer__inner',
      },
      {
        id: 'cart-icon-bubble',
        section: 'cart-icon-bubble',
        selector: '.shopify-section',
      },
    ];
  }
}

customElements.define('cart-drawer-items', CartDrawerItems);


// Empty-cart bundle card: keep price, compare-at and "Save $X" in sync with the
// selected swatch. (Image + button URL are handled by <product-card-color-swatch>.)
document.addEventListener('change', (e) => {
  const swatch = e.target;
  if (!swatch.matches || !swatch.matches('.cart-empty-bundle-card .product-card-color-swatch')) return;

  const card = swatch.closest('.cart-empty-bundle-card');
  const mapEl = card && card.querySelector('[data-bundle-variants]');
  if (!mapEl) return;

  let map;
  try { map = JSON.parse(mapEl.textContent); } catch (err) { return; }
  const v = map[swatch.dataset.variantId];
  if (!v) return;

  const price = card.querySelector('.cart-empty-bundle-card__price');
  const compare = card.querySelector('.cart-empty-bundle-card__compare');
  const subtitle = card.querySelector('.cart-empty-bundle-card__subtitle');

  if (price) price.textContent = v.price;
  if (compare) {
    compare.textContent = v.compare || '';
    compare.hidden = !v.compare;
  }
  if (subtitle) subtitle.textContent = v.save ? `Save ${v.save}` : (subtitle.dataset.defaultText || '');
});